/**
 * Overview — alert-led, on live data (task 42, after task 53).
 *
 * With a Client selected it answers the three questions an analyst arrives
 * with: what is waiting for me (open alerts by band, and the top of the
 * queue), is the volume normal (14 days of detections, and how many became
 * alerts), and is anything not being collected. With none selected it is
 * the cross-Client summary (Story 7), which is the operator's real start
 * screen: which Client needs me first.
 *
 * Deliberately NOT a posture dashboard; see the 2026-09-17 amendment to
 * Story 7.
 */

import { useQuery } from '@tanstack/react-query';
import { Link, useNavigate } from '@tanstack/react-router';

import {
  getCrossClient,
  getOverview,
  type Alert,
  type CountsByBand,
  type DetectionToAlert,
  type Pipeline,
  type Severity,
  type SourceInfo,
} from '@bluesphere/api-client';
import {
  DetectionVolumeChart,
  MetricTile,
  PipelineStrip,
  SEVERITY_COLORS,
  SeverityChip,
  colors,
} from '@bluesphere/ui-kit';

import { useAccessToken } from '../auth/useAccessToken';
import { useSelectedTenant } from '../auth/useSelectedTenant';
import { EmptyState, ErrorState, HealthChip, Loading, SourceBadge } from '../components/states';
import { shortDay, sourceLabel, statusLabel, timeAgo } from '@bluesphere/mdr-alert-panel';
import { isInService } from '../lib/clientStatus';

export function Overview() {
  const { tenantId } = useSelectedTenant();
  const idToken = useAccessToken();

  const overview = useQuery({
    queryKey: ['overview', tenantId],
    queryFn: () => getOverview(tenantId as string, idToken),
    enabled: Boolean(tenantId && idToken),
    refetchInterval: 60_000,
  });

  if (!tenantId) return <CrossClientOverview />;

  if (overview.isError) {
    return (
      <ErrorState
        title="Could not load the Overview"
        error={overview.error}
        onRetry={() => void overview.refetch()}
      />
    );
  }

  const data = overview.data;
  const counts = data?.counts_by_band;

  return (
    <div className="space-y-6">
      <div className="bs-card p-5">
        <div className="mb-4 flex flex-wrap items-baseline justify-between gap-2">
          <h2 className="text-xl font-bold lg:text-2xl">Pipeline</h2>
          <span className="text-sm text-bluesphere-muted">
            What came in, what was noticed, what needs a person · {data?.pipeline.window_days ?? 14}{' '}
            days
          </span>
        </div>
        {data ? <PipelineCard pipeline={data.pipeline} sources={data.sources} /> : <Loading />}
      </div>

      <div className="bs-card p-5">
        <div className="mb-4 flex items-center justify-between">
          <h2 className="text-xl font-bold lg:text-2xl">Open alerts</h2>
          {data && (
            <Link to="/alerts" className="text-sm text-bluesphere-muted underline">
              {data.open_total} total · open the queue
            </Link>
          )}
        </div>
        {counts && data ? (
          <BandTiles counts={counts} ratio={data.detection_to_alert} />
        ) : (
          <Loading />
        )}
      </div>

      <div className="bs-card p-5">
        <div className="mb-3 flex flex-wrap items-baseline justify-between gap-2">
          <h2 className="text-xl font-bold lg:text-2xl">14-day detection volume</h2>
          {data && <RatioLine ratio={data.detection_to_alert} />}
        </div>
        {data ? (
          <DetectionVolumeChart
            data={data.volume.map((p) => ({
              label: shortDay(p.date),
              critical: p.critical,
              high: p.high,
              elevated: p.medium,
              normal: p.low + p.informational,
            }))}
          />
        ) : (
          <Loading height="h-[240px]" />
        )}
      </div>

      <div className="grid gap-6 lg:grid-cols-2">
        <div className="bs-card p-5">
          <h2 className="mb-3 text-xl font-bold lg:text-2xl">Ingestion health</h2>
          {data ? <SourceHealthList sources={data.sources} /> : <Loading />}
        </div>

        <div className="bs-card p-5">
          <h2 className="mb-3 text-xl font-bold lg:text-2xl">Needs attention</h2>
          {data ? <AttentionList alerts={data.attention} /> : <Loading />}
        </div>
      </div>
    </div>
  );
}

/**
 * Events → Detections → Playbook → Alerts, with the detection-to-alert
 * ratio on the last stage. The Playbook stage says "approx." when any day
 * in the window predates the per-outcome counters (task 68). Each stage
 * opens its section: Events → the Events page, Detections and Playbook →
 * the Detections stream (where the playbook's outcomes are shown per
 * detection), Alerts → the queue.
 *
 * There is no Sources stage (Steve, 2026-09-29): beside Events it put two
 * health numbers side by side that counted different things. Events is
 * the one intake stage and says whether everything feeding the pipeline
 * is reading; Ingestion health below and the Integrations page carry the
 * per-source detail.
 */
function PipelineCard({
  pipeline,
  sources,
}: {
  pipeline: Pipeline;
  sources: Record<string, SourceInfo>;
}) {
  const navigate = useNavigate();
  const { playbook } = pipeline;
  const plural = (n: number, word: string) => `${n} ${word}${n === 1 ? '' : 's'}`;
  const intake = intakeSummary(sources);
  return (
    <PipelineStrip
      stages={[
        {
          label: 'Events',
          value: intake.value,
          sub: intake.sub,
          color: colors.primary,
          tint: '#EEF3FA',
          onClick: () => void navigate({ to: '/events', search: {} }),
        },
        {
          label: 'Detections',
          value: pipeline.detections,
          sub: `${pipeline.window_days} days, all sources`,
          color: colors.scoreElevated,
          tint: colors.tintElevated,
          onClick: () => void navigate({ to: '/detections', search: {} }),
        },
        {
          label: 'Playbook',
          value: playbook.raised,
          sub: `raised · ${plural(playbook.resolved, 'resolved')}${
            playbook.watch ? ` · ${playbook.watch} watching` : ''
          }${playbook.derived ? ' · approx.' : ''}`,
          color: colors.scoreHigh,
          tint: colors.tintHigh,
          onClick: () => void navigate({ to: '/detections', search: {} }),
        },
        {
          label: 'Alerts',
          value: pipeline.alerts,
          sub:
            pipeline.ratio === null
              ? 'none raised'
              : `1 alert per ${pipeline.ratio.toFixed(1)} detections`,
          color: colors.scoreCritical,
          tint: colors.tintCritical,
          onClick: () => void navigate({ to: '/alerts' }),
        },
      ]}
    />
  );
}

/**
 * The Events stage (task 158): intake health, not a count. The event
 * store keeps no counter, and counting on each Overview load would be an
 * Athena scan per page view; a per-day count written by the events Lambda
 * can fill the value later.
 *
 * One number over everything feeding the pipeline: a detection source is
 * one input, an event source is one input per feed (its feeds read and
 * fail independently; sign-ins can be unlicensed while audits read). The
 * value is the inputs reading; the line names the rest.
 */
export function intakeSummary(sources: Record<string, SourceInfo>): {
  value: number | string;
  sub: string;
} {
  let reading = 0;
  let total = 0;
  const other = { degraded: 0, error: 0, unlicensed: 0, pending: 0 };
  for (const source of Object.values(sources)) {
    if (source.state === 'removed') continue;
    if (source.feeds) {
      for (const feed of Object.values(source.feeds)) {
        total += 1;
        if (feed?.state === 'ok') reading += 1;
        else if (feed?.state === 'error') other.error += 1;
        else if (feed?.state === 'unlicensed') other.unlicensed += 1;
        else other.pending += 1;
      }
    } else {
      total += 1;
      if (source.health === 'healthy') reading += 1;
      else if (source.health === 'degraded') other.degraded += 1;
      else other.pending += 1;
    }
  }
  if (total === 0) return { value: '—', sub: 'nothing connected' };
  const parts = [
    other.degraded ? `${other.degraded} degraded` : '',
    other.error ? `${other.error} in error` : '',
    other.unlicensed ? `${other.unlicensed} unlicensed` : '',
    other.pending ? `${other.pending} not yet read` : '',
  ].filter(Boolean);
  const noun = total === 1 ? 'input' : 'inputs';
  return {
    value: reading,
    sub: parts.length
      ? `of ${total} ${noun} reading · ${parts.join(' · ')}`
      : `${noun} reading · all healthy`,
  };
}

const BAND_TILES: Array<{
  key: keyof CountsByBand;
  band: Severity;
  color: string;
  tint: string;
}> = [
  { key: 'critical', band: 'Critical', color: colors.scoreCritical, tint: colors.tintCritical },
  { key: 'high', band: 'High', color: colors.scoreHigh, tint: colors.tintHigh },
  { key: 'medium', band: 'Medium', color: colors.scoreElevated, tint: colors.tintElevated },
  { key: 'low', band: 'Low', color: colors.scoreNormal, tint: colors.tintNormal },
  {
    key: 'informational',
    band: 'Informational',
    color: colors.scoreNormal,
    tint: colors.tintNormal,
  },
];

/**
 * Open alerts per band, each tile a link into the queue filtered to that
 * band and the active lifecycle (task 88). No date range on the link: the
 * count is the ACTIVE lifecycle over all time, so any range would make the
 * tile and the queue disagree. One tile per band — the filter takes one
 * band, so Low and Informational are no longer summed. With no Client
 * selected `/alerts` is the all-Clients queue, so the same link serves
 * the cross-Client totals.
 */
function BandTiles({ counts, ratio }: { counts: CountsByBand; ratio?: DetectionToAlert }) {
  return (
    <div className="flex flex-wrap gap-3">
      {BAND_TILES.map((t) => (
        <Link
          key={t.key}
          to="/alerts"
          search={{ severity: t.band, status: 'active' }}
          aria-label={`${counts[t.key]} open ${t.band} alert${counts[t.key] === 1 ? '' : 's'}: open the queue`}
          className="flex min-w-[130px] flex-1 rounded-card transition-transform hover:scale-[1.02] focus:outline-hidden focus-visible:ring-2 focus-visible:ring-[#76B7CD]"
        >
          <MetricTile
            label={t.band}
            value={counts[t.key]}
            color={t.color}
            tint={t.tint}
            className="w-full"
          />
        </Link>
      ))}
      {ratio && (
        <MetricTile
          label="Detections per alert · 14d"
          value={ratio.ratio === null ? '—' : ratio.ratio.toFixed(1)}
          color={colors.primary}
          tint="#EEF3FA"
        />
      )}
    </div>
  );
}

function RatioLine({ ratio }: { ratio: DetectionToAlert }) {
  if (ratio.detections === 0) return null;
  return (
    <span className="text-sm text-bluesphere-muted">
      {ratio.detections} detection{ratio.detections === 1 ? '' : 's'} → {ratio.alerts} alert
      {ratio.alerts === 1 ? '' : 's'}
      {ratio.ratio !== null && ratio.alerts > 0 && ` · ${ratio.ratio.toFixed(1)} : 1`}
    </span>
  );
}

function SourceHealthList({ sources }: { sources: Record<string, SourceInfo> }) {
  const entries = Object.entries(sources);
  if (entries.length === 0) {
    return (
      <EmptyState
        title="No sources connected"
        body="Connect Defender (admin consent) or SentinelOne (console URL and token) on the Clients page."
      />
    );
  }
  return (
    <div className="space-y-2">
      {entries.map(([name, info]) => (
        <div
          key={name}
          className="bs-row flex flex-wrap items-center justify-between gap-2 px-4 py-2.5"
        >
          <div className="min-w-0">
            <div className="text-sm font-semibold">{sourceLabel(name)}</div>
            <div className="text-xs text-bluesphere-muted">
              {info.state === 'removed'
                ? 'removed'
                : info.last_success_at
                  ? `synced ${timeAgo(info.last_success_at)} · ${info.detections_24h ?? 0} in 24h`
                  : 'never synced'}
              {info.backfilling && ' · back-filling'}
            </div>
            {info.last_error && info.health !== 'healthy' && (
              <div className="mt-0.5 truncate text-xs text-red-700" title={info.last_error}>
                {info.last_error}
              </div>
            )}
          </div>
          <HealthChip status={info.state === 'removed' ? 'pending' : (info.health ?? 'pending')} />
        </div>
      ))}
    </div>
  );
}

function AttentionList({ alerts }: { alerts: Alert[] }) {
  if (alerts.length === 0) {
    return (
      <EmptyState
        title="No open alerts"
        body="Nothing is waiting for review. Detections the playbook resolved are on the Detections stream."
      />
    );
  }
  return (
    <div className="bs-scroll max-h-[320px] space-y-2 overflow-y-auto pr-1">
      {alerts.map((a) => (
        <Link
          key={a.alert_id}
          to="/alerts/$alertRef"
          params={{ alertRef: a.short_id }}
          className="bs-row flex items-center justify-between gap-3 px-4 py-2.5 transition-shadow hover:shadow-cardHover"
        >
          <div className="min-w-0">
            <div className="flex items-center gap-2">
              <span className="font-mono text-xs font-semibold text-bluesphere-primary">
                {a.short_id}
              </span>
              <span className="truncate text-sm font-semibold">{a.title}</span>
            </div>
            <div className="mt-1 flex flex-wrap items-center gap-2">
              <SeverityChip severity={a.severity} />
              {a.sources.map((s) => (
                <SourceBadge key={s} label={sourceLabel(s)} />
              ))}
              <span className="text-xs text-bluesphere-muted">
                {statusLabel(a.status)} · {a.member_count} detection
                {a.member_count === 1 ? '' : 's'} · {timeAgo(a.detected_at)}
              </span>
            </div>
          </div>
          <div
            className="text-xl font-bold tabular-nums"
            style={{ color: SEVERITY_COLORS[a.severity] }}
          >
            {a.score}
          </div>
        </Link>
      ))}
    </div>
  );
}

// ---------- No Client selected: the cross-Client summary ----------

const BAND_ORDER: Array<{ key: keyof CountsByBand; label: string; color: string }> = [
  { key: 'critical', label: 'Critical', color: colors.scoreCritical },
  { key: 'high', label: 'High', color: colors.scoreHigh },
  { key: 'medium', label: 'Medium', color: colors.scoreElevated },
  { key: 'low', label: 'Low', color: colors.scoreNormal },
  { key: 'informational', label: 'Informational', color: colors.scoreNormal },
];

/** Every visible Client's open alerts per band: the all-Clients queue's counts. */
function totalByBand(rows: Array<{ counts_by_band?: CountsByBand | null }>): CountsByBand {
  const total: CountsByBand = { critical: 0, high: 0, medium: 0, low: 0, informational: 0 };
  for (const row of rows) {
    for (const band of BAND_ORDER) total[band.key] += row.counts_by_band?.[band.key] ?? 0;
  }
  return total;
}

function CrossClientOverview() {
  const idToken = useAccessToken();
  const { setTenantId, orgId } = useSelectedTenant();
  const summary = useQuery({
    queryKey: ['overview', 'cross-client', orgId],
    queryFn: () => getCrossClient(idToken, { org: orgId ?? undefined }),
    enabled: Boolean(idToken),
    refetchInterval: 60_000,
  });

  return (
    <div className="bs-card p-5">
      <div className="mb-4 flex items-center justify-between">
        <h2 className="text-xl font-bold lg:text-2xl">All Clients</h2>
        <span className="text-sm text-bluesphere-muted">Open alerts by severity</span>
      </div>

      {summary.isPending && <Loading />}
      {summary.isError && (
        <ErrorState
          title="Could not load the cross-Client summary"
          error={summary.error}
          onRetry={() => void summary.refetch()}
        />
      )}
      {summary.data && summary.data.clients.length === 0 && (
        <EmptyState
          title="No Clients yet"
          body="Add a Client on the Clients page to start ingesting."
        />
      )}
      {summary.data && summary.data.clients.length > 0 && (
        <div className="mb-4">
          <BandTiles counts={totalByBand(summary.data.clients)} />
        </div>
      )}
      {summary.data && summary.data.clients.length > 0 && (
        <div className="space-y-2">
          {summary.data.clients
            .slice()
            .sort((a, b) => b.open_total - a.open_total)
            .map((row) => (
              <button
                key={row.client_tenant_id}
                type="button"
                onClick={() => setTenantId(row.client_tenant_id)}
                disabled={!isInService(row)}
                className="bs-row flex w-full flex-wrap items-center justify-between gap-3 px-4 py-3 text-left transition-shadow hover:shadow-cardHover disabled:cursor-default disabled:opacity-70"
              >
                <div className="min-w-0">
                  <div className="text-sm font-semibold">{row.display_name}</div>
                  <div className="text-xs text-bluesphere-muted">
                    {isInService(row)
                      ? `${row.open_total} open alert${row.open_total === 1 ? '' : 's'}`
                      : row.status === 'pending'
                        ? 'awaiting consent'
                        : 'revoked'}
                  </div>
                </div>
                {row.counts_by_band && (
                  <div className="flex flex-wrap gap-2">
                    {BAND_ORDER.map((band) => (
                      <span
                        key={band.key}
                        className="bs-chip bg-bluesphere-surfaceMuted"
                        title={band.label}
                      >
                        <span
                          aria-hidden="true"
                          className="inline-block h-2 w-2 rounded-full"
                          style={{ backgroundColor: band.color }}
                        />
                        {row.counts_by_band?.[band.key] ?? 0}
                      </span>
                    ))}
                  </div>
                )}
              </button>
            ))}
        </div>
      )}
    </div>
  );
}
